import { DistributionChannel, NotificationTemplate, TemplateViewModel } from '@ail/notifications-api'
import { useTranslation } from 'react-i18next'
import { useCallback, useMemo, useState } from 'react'
import { printDate } from '../../../helpers/Utils'
import { useGetTemplatesQuery } from '../../../redux/api/notificationsApi'
import { Grid, GridColumn } from '../../common/Grid/Grid'
import NotificationGroupDetails from './NotificationGroupDetails'
import './NotificationsAdminPanel.scss'
import { Loader } from '../../common/Loader/Loader'

export interface CreateTemplateTarget {
  name: string
  channel: DistributionChannel
  initialLang: string
  missingLangs: string[]
}
const RENDER_DETAILS_ROW_HEIGHT = 100
const EDITOR_HEIGHT = 100
function hasNativeFlag(flags?: DistributionChannel) {
  return flags === DistributionChannel.Native
}
function hasEmailFlag(flags?: DistributionChannel) {
  return flags === DistributionChannel.Email
}

export interface NotificationsDataRow {
  name: string
  templates: TemplateViewModel[]
  hasEmail: boolean
  hasNative: boolean
  hasNone: boolean
  lastUpdate: string
  status: 'complete' | 'incomplete' | 'empty'
}

export default function NotificationsAdminPanel() {
  // const [createTarget, setCreateTarget] = useState<CreateTemplateTarget | undefined>()
  // const [newTemplate, setNewTemplate] = useState<boolean>()
  // const closeModal = useCallback(() => {
  //   setCreateTarget(undefined)
  // }, [])

  const { t } = useTranslation('common')
  const templates = useGetTemplatesQuery()
  const [detailsVisibility, setDetailsVisibility] = useState<Record<string, number | undefined>>({})
  const rows = useMemo(() => {
    const baseGroups = Object.fromEntries(
      Object.values(NotificationTemplate).map((k) => [k, [] as TemplateViewModel[]])
    ) as Record<NotificationTemplate, TemplateViewModel[]>

    const groups = (templates.currentData ?? [])
      .filter((val) => val.name)
      .reduce((acc, n) => {
        ;(acc[n.name as NotificationTemplate] ??= []).push(n)
        return acc
      }, baseGroups)

    return Object.entries(groups).map(([name, templates]) => {
      const hasEmail = templates.some((t) => hasEmailFlag(t.channel))
      const hasNative = templates.some((t) => hasNativeFlag(t.channel))
      const status: NotificationsDataRow['status'] =
        templates.length === 0 ? 'empty' : templates.length >= 8 ? 'complete' : 'incomplete'
      return {
        name,
        templates,
        hasEmail,
        hasNative,
        hasNone: !hasEmail && !hasNative,
        lastUpdate:
          templates
            .map((t) => t.updatedAt)
            .sort()
            .at(-1) ?? '',
        status,
      } as NotificationsDataRow
    })
  }, [templates.currentData])

  const columns = useMemo<GridColumn<NotificationsDataRow>[]>(
    () => [
      {
        key: 'name',
        name: 'Name',
        width: 350,
        cellClass: (row) => `name ${row.status}`,
        filter: {
          type: 'inline_text_filter',
          filterFn: (row, value) => row.name.toLowerCase().includes(value.toLowerCase()),
          customPlaceholder: 'Search by name',
        },
        renderCell: ({ row }) => row.name,
        sortBy: (row) => row.name,
      },
      {
        key: 'flags',
        name: 'Flags',
        width: 100,
        cellClass: 'flags',
        renderCell: ({ row }) => (
          <div className="flags">
            {row.hasNative && <i className={`icon-alert notif-icon-toggle`} title="Native" />}{' '}
            {row.hasEmail && <i className={`icon-mail notif-icon-toggle`} title="Email" />}
          </div>
        ),
      },
      {
        key: 'blank',
        name: '',
        MinWidth: 100,
        cellClass: 'blank',
        // filter: {
        //   ignoreSaved: true,
        //   position: 'header-center',
        //   type: 'inline_button_filter',
        //   icon: 'icon-add',
        //   customClass: 'no-bg',
        //   filterFn: () => true,
        //   onChange: () => {
        //     setCreateTarget({
        //       name: '',
        //       flag: DistributionChannel.Native,
        //       initialLang: 'en',
        //       missingLangs: [],
        //     })
        //   },
        // },
      },
      {
        key: 'updatedAt',
        name: 'Last update',
        width: 200,
        cellClass: 'date',
        renderCell: ({ row }) => (
          <div className="flex between">
            <div>{printDate(row.lastUpdate, true)}</div>
            <div
              onClick={() => {
                setDetailsVisibility((current) => {
                  return {
                    ...current,
                    [row.name]: current[row.name] ? undefined : RENDER_DETAILS_ROW_HEIGHT,
                  }
                })
              }}
            >
              <i className={`${detailsVisibility[row.name] ? 'icon-up' : 'icon-bottom'}`}></i>
            </div>
          </div>
        ),
        sortBy: (row) => (row.lastUpdate ? new Date(row.lastUpdate) : undefined),
        sortDescendingFirst: true,
      },
    ],
    [detailsVisibility]
  )

  //* if toggled sets GridDetailsHeight to 220, else returns to original
  const expandEditor = useCallback(
    (editorCount: number, row: NotificationsDataRow) =>
      setDetailsVisibility((current) => {
        return {
          ...current,
          [row.name]: RENDER_DETAILS_ROW_HEIGHT + editorCount * EDITOR_HEIGHT,
        }
      }),
    []
  )
  return (
    <Loader className="grid-size notifications-admin-panel" blocking={templates.isFetching}>
      <Grid
        id="NotificationsAdminPanel"
        title={t('NOTIFICATIONS_MANAGEMENT')}
        icon="icon-notification"
        customClass="adminPanelGrid"
        columns={columns}
        rows={rows}
        rowKeyGetter={(row) => row.name}
        onReload={() => templates.refetch()}
        hideExport
        hideReorder
        detailsVisibility={detailsVisibility}
        renderDetails={(row) => (
          <NotificationGroupDetails row={row} onEditorToggle={(toggled) => expandEditor(toggled, row)} />
        )}
      />

      {/* {createTarget && (
        <CreateTemplate
          name={createTarget.name}
          flag={createTarget.flag}
          initialLang={createTarget.initialLang}
          missingLangs={createTarget.missingLangs}
          onClose={closeModal}
        />
      )} */}
    </Loader>
  )
}
